import React from "react"
import { MarkupComponents } from "@indeed/frontend-markup-react-components"
import { Box, Button, Flex, FlexProps, Polymorphic, Text, TextLink, VisuallyHidden } from "@indeed/ifl-components"
import { GettextInterface } from "@indeed/indeed-gettext"
import { configSelector } from "../../common/utils/config-slice"
import { useI18n } from "../../common/utils/i18n-context"
import { useAppSelector } from "../../common/utils/redux-helpers"
import { IPreferenceCategory } from "./feedback-form-model"
import { handleStickyBoxOverlap } from "./helpers"
import { PreferenceSlashableIcon } from "./preference-slashable-icon"

export type IPreferenceItemProps = Omit<FlexProps, "as"> & {
    category: IPreferenceCategory
    selected: boolean
    bodyText: string
    toggleSelect?: (toggleState: boolean) => any
    buttonAriaLabel?: string
    id?: string
    additionalSelectionsLabel?: string
}

type PreferenceItemComponent = Polymorphic.Component<"li", IPreferenceItemProps>

export function getPreferenceCategoryNamesMap(gettext: GettextInterface["gettext"]): Record<IPreferenceCategory, string> {
    return {
        JOB_TITLE: gettext("Job title"),
        JOB_TYPE: gettext("Job type"),
        SCHEDULE: gettext("Schedule"),
        PAY: gettext("Pay"),
        LOCATION: gettext("Location"),
        REMOTE: gettext("Remote"),
        COMPANY: gettext("Company"),
        JOB_CATEGORY: gettext("Job category"),
        LANGUAGE: gettext("Language"),
        EDUCATION: gettext("Education"),
        CERTIFICATION: gettext("License / certification"),
        LICENSE: gettext("License / certification"),
        SKILL: gettext("Skill"),
    }
}

export const PreferenceItem = ((props, _context) => {
    const {
        id,
        sx,
        category,
        selected,
        bodyText,
        buttonAriaLabel,
        toggleSelect,
        as = "li",
        additionalSelectionsLabel,
        ...flexProps
    } = props
    const { gettext } = useI18n()

    const { proctorGroups, profileUrl } = useAppSelector(configSelector)
    const useTextLabelForCategory = proctorGroups.jsj_mrp_jcr_feedback_design_tst === 1
    const showNewContentStrings = proctorGroups.jsj_mrp_jcr_content_strings_tst === 1

    const notInterestedText = showNewContentStrings ? gettext("Hide") : gettext("Not interested")
    const undoText = showNewContentStrings ? gettext("Unhide") : gettext("Undo")
    const payModalPath = "/preferences?open=pay"
    const payPreferenceUrl = `${profileUrl}${payModalPath}`

    // dynamically get the icon based on the category prop
    return (
        <Flex
            {...flexProps}
            as={as}
            id={id}
            sx={{
                borderBlockEnd: "1px solid",
                borderBlockEndColor: "border.weak",
                marginBlockEnd: 0,
                marginBlockStart: 0,
                marginInlineEnd: 0,
                marginInlineStart: 0,
                paddingBlockEnd: 4,
                paddingBlockStart: 4,
                paddingInlineEnd: 4,
                paddingInlineStart: 4,
                ...(useTextLabelForCategory
                    ? {
                          marginInlineEnd: 4,
                          marginInlineStart: 4,
                          paddingInlineEnd: 0,
                          paddingInlineStart: 0,
                      }
                    : {}),
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
                ...sx,
            }}
            // Used by the Shield UI tests
            data-testid="feedback-form-preference-item"
            aria-live="polite"
            onFocus={handleStickyBoxOverlap}
        >
            {!useTextLabelForCategory && (
                <>
                    <PreferenceSlashableIcon category={category} isSlashed={selected} />
                    <Box sx={{ paddingInlineEnd: 4, paddingInlineStart: 4, flex: "1", minInlineSize: "auto" }}>
                        <Text aria-atomic="true" variant="contrast" level={3} as="div">
                            {!selected && <VisuallyHidden>{`${getPreferenceCategoryNamesMap(gettext)[category]}:`}</VisuallyHidden>}
                            <MarkupComponents
                                components={{
                                    strongText: "strong",
                                    br: () => <br />,
                                    payPreferencesEl: (children: string) => (
                                        <TextLink href={payPreferenceUrl} target="_blank" rel="noopener noreferrer">
                                            {children}
                                        </TextLink>
                                    ),
                                }}
                            >
                                {bodyText}
                            </MarkupComponents>
                            {selected && additionalSelectionsLabel && <VisuallyHidden>{additionalSelectionsLabel}</VisuallyHidden>}
                        </Text>
                    </Box>
                </>
            )}
            {useTextLabelForCategory && (
                <Box
                    sx={{
                        paddingBlockEnd: 0,
                        paddingBlockStart: 0,
                        paddingInlineEnd: 0,
                        paddingInlineStart: 0,
                        flex: "1",
                        minInlineSize: "auto",
                    }}
                >
                    <Flex sx={{ flexDirection: "column" }}>
                        {!selected && (
                            <Text
                                aria-live="polite"
                                variant="secondary"
                                level={3}
                                as="h3"
                                sx={{ marginBlockEnd: 0, marginBlockStart: 0, marginInlineEnd: 0, marginInlineStart: 0 }}
                            >
                                {getPreferenceCategoryNamesMap(gettext)[category]}
                            </Text>
                        )}
                        <Text
                            aria-live="polite"
                            variant="contrast"
                            level={3}
                            as={selected ? "div" : "strong"}
                            sx={{ fontWeight: selected ? "normal" : "bold" }}
                        >
                            {selected ? (
                                <MarkupComponents
                                    components={{
                                        strongText: "strong",
                                        br: () => <br />,
                                        payPreferencesEl: (children: string) => (
                                            <TextLink href={payPreferenceUrl} target="_blank" rel="noopener noreferrer">
                                                {children}
                                            </TextLink>
                                        ),
                                    }}
                                >
                                    {bodyText}
                                </MarkupComponents>
                            ) : (
                                bodyText
                            )}
                        </Text>
                    </Flex>
                </Box>
            )}
            <Box>
                <Button
                    variant={selected ? "text" : "secondary"}
                    size={["sm", "md"]}
                    sx={{
                        // HACK: this should be fixed in IFL. The button's hover styling gets "stuck" on mobile.
                        //  This media query fixes the hover styling here on devices that don't support hovering
                        "@media (hover: none)": {
                            "&:hover": {
                                color: "foreground.primary",
                            },
                        },
                    }}
                    onClick={() => toggleSelect?.call(null, selected)}
                    aria-label={buttonAriaLabel}
                    data-testid="not-interested-button"
                    type="button"
                    aria-atomic="true"
                >
                    {selected ? undoText : notInterestedText}
                </Button>
            </Box>
        </Flex>
    )
}) as PreferenceItemComponent
